/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import { EmptyState } from '../../../components/v2';
import useNsirHistorico from './hooks/useNsirHistorico';
import {
  formatAuditTimeOnly,
  formatAuditTimestamp,
  getAuditActor,
  getAuditFieldLines,
  groupHistoricoByDate,
} from './utils/formatAuditHistorico';

const AuditTimelineCard = ({ item, showTimestampInCard }) => {
  const lines = getAuditFieldLines(item);

  return (
    <Box
      sx={{
        p: 1,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
      }}
    >
      {showTimestampInCard && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25, lineHeight: 1.3 }}>
          {formatAuditTimestamp(item.timestamp)}
        </Typography>
      )}
      <Typography
        variant="subtitle2"
        fontWeight={600}
        fontSize="0.8125rem"
        sx={{ mb: lines.length ? 0.5 : 0, lineHeight: 1.35 }}
      >
        {getAuditActor(item)}
      </Typography>
      {lines.length > 0 ? (
        <Stack component="ul" spacing={0.25} sx={{ m: 0, pl: 1.75 }}>
          {lines.map((line, index) => (
            <Typography
              key={index}
              component="li"
              variant="body2"
              color="text.secondary"
              fontSize="0.8125rem"
              sx={{ display: 'list-item', lineHeight: 1.4 }}
            >
              {line}
            </Typography>
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {item.message || '—'}
        </Typography>
      )}
    </Box>
  );
};

AuditTimelineCard.propTypes = {
  item: PropTypes.object.isRequired,
  showTimestampInCard: PropTypes.bool,
};

AuditTimelineCard.defaultProps = {
  showTimestampInCard: false,
};

const AuditTab = ({ nsirId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { items, loading, loadError, refetch } = useNsirHistorico(nsirId, {
    enabled: Boolean(nsirId),
  });

  const groups = groupHistoricoByDate(items);

  const tabPanelBleedSx = {
    m: { xs: -2, sm: -2.5 },
    p: { xs: 1.5, sm: 2 },
    pt: { xs: 0.25, sm: 0.375 },
  };

  return (
    <Box sx={tabPanelBleedSx}>
      <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ mb: 0.75, minHeight: 28 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<RefreshIcon fontSize="small" />}
          onClick={refetch}
          disabled={loading || !nsirId}
          sx={{ minHeight: 30, py: 0.25 }}
        >
          Atualizar
        </Button>
      </Stack>

      {loading && items.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {!loading && loadError && (
        <Stack spacing={1} alignItems="center" sx={{ py: 2 }}>
          <Typography variant="h6" color="text.secondary">
            Histórico indisponível
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Não foi possível carregar o histórico de auditoria no momento.
          </Typography>
          <Button variant="outlined" size="small" onClick={refetch}>
            Tentar novamente
          </Button>
        </Stack>
      )}

      {!loading && !loadError && items.length === 0 && (
        <EmptyState
          size="compact"
          title="Nenhuma alteração registrada"
          message="O histórico aparecerá aqui quando houver mudanças nesta NSIR."
        />
      )}

      {items.length > 0 && (
        <Box sx={{ maxHeight: { xs: 'none', md: 'calc(100vh - 260px)' }, overflow: 'auto', pr: 0.25 }}>
          <Timeline
            position="right"
            sx={{
              m: 0,
              p: 0,
              '& .MuiTimelineItem-root': { minHeight: 'auto' },
              '& .MuiTimelineItem-root:before': { flex: 0, padding: 0 },
            }}
          >
            {groups.map((entry) => {
              if (entry.type === 'date') {
                return (
                  <TimelineItem key={entry.key}>
                    <TimelineContent sx={{ py: 0.5, px: 0 }}>
                      <Typography
                        variant="overline"
                        color="text.secondary"
                        sx={{ letterSpacing: 0.8, fontWeight: 600, lineHeight: 1.2, fontSize: '0.6875rem' }}
                      >
                        {entry.label}
                      </Typography>
                    </TimelineContent>
                  </TimelineItem>
                );
              }

              const { item } = entry;
              return (
                <TimelineItem key={entry.key}>
                  {!isMobile && (
                    <TimelineOppositeContent
                      sx={{
                        flex: 0.12,
                        maxWidth: 76,
                        m: 'auto 0',
                        pr: 0.75,
                        py: 0.25,
                      }}
                      color="text.secondary"
                      variant="caption"
                      fontSize="0.6875rem"
                    >
                      {formatAuditTimeOnly(item.timestamp)}
                    </TimelineOppositeContent>
                  )}
                  <TimelineSeparator sx={{ py: 0.25 }}>
                    <TimelineDot variant="outlined" color="primary" sx={{ m: 0, boxShadow: 'none', p: 0.375 }} />
                    <TimelineConnector sx={{ my: 0.25 }} />
                  </TimelineSeparator>
                  <TimelineContent sx={{ py: 0.25, px: { xs: 0, sm: 0.75 } }}>
                    <AuditTimelineCard item={item} showTimestampInCard={isMobile} />
                  </TimelineContent>
                </TimelineItem>
              );
            })}
          </Timeline>
        </Box>
      )}
    </Box>
  );
};

AuditTab.propTypes = {
  nsirId: PropTypes.string,
};

AuditTab.defaultProps = {
  nsirId: null,
};

export default AuditTab;
